import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import type { GeneratedArtifactViewState } from "@src/shared/domain/generated-artifact";
import { cn } from "@src/shared/lib/utils";

const DEFAULT_GENERATING_SKELETON_WIDTHS = ["w-full", "w-[92%]", "w-[85%]"];

export interface GeneratedArtifactPanelStateProps {
  viewState: GeneratedArtifactViewState;
  isStillGenerating?: boolean;
  /** aria-label while generating; the spinner copy appends an ellipsis. */
  generatingLabel: string;
  emptyMessage: string;
  failedMessage: string;
  errorFallback: string;
  /** Tailwind width classes for the generating skeleton rows. */
  generatingSkeletonWidths?: string[];
  /** Rendered when the artifact is ready and has at least one item. */
  children: ReactNode;
}

function PanelStatusMessage({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="px-4 py-3 text-[13px] text-muted-foreground"
    >
      {children}
    </p>
  );
}

function GeneratingSkeleton({
  label,
  widths,
}: {
  label: string;
  widths: string[];
}) {
  return (
    <div
      className="space-y-2 px-4 py-3"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
    >
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        {label}…
      </div>
      {widths.map((width) => (
        <Skeleton key={width} className={cn("h-8", width)} />
      ))}
    </div>
  );
}

function StillGeneratingBanner() {
  return (
    <p
      role="status"
      aria-live="polite"
      className="px-3 py-2 text-[12px] text-muted-foreground"
    >
      Still checking for more…
    </p>
  );
}

/**
 * Shared view-state UI for generated encounter artifacts (agenda, action
 * items, codes). Dispatches on `GeneratedArtifactViewState` and leaves the
 * ready-state markup to the caller via `children`.
 */
export function GeneratedArtifactPanelState({
  viewState,
  isStillGenerating = false,
  generatingLabel,
  emptyMessage,
  failedMessage,
  errorFallback,
  generatingSkeletonWidths = DEFAULT_GENERATING_SKELETON_WIDTHS,
  children,
}: GeneratedArtifactPanelStateProps) {
  if (viewState === "fetch-error") {
    return (
      <ErrorMessage
        className="block px-4 py-3"
        error={true}
        fallback={errorFallback}
        sizing="xs"
      />
    );
  }

  if (viewState === "generation-failed") {
    return <PanelStatusMessage>{failedMessage}</PanelStatusMessage>;
  }

  if (viewState === "generating") {
    return (
      <GeneratingSkeleton
        label={generatingLabel}
        widths={generatingSkeletonWidths}
      />
    );
  }

  if (viewState === "empty" || viewState === "not-started") {
    return <PanelStatusMessage>{emptyMessage}</PanelStatusMessage>;
  }

  return (
    <>
      {isStillGenerating ? <StillGeneratingBanner /> : null}
      {children}
    </>
  );
}
